import { useEffect, useState } from "react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { Plus, Trash2, Shield } from "lucide-react";

const ROLE_STYLES = {
  admin: "bg-[hsl(148_40%_88%)] text-[hsl(var(--forest))]",
  manager: "bg-[hsl(var(--brass)/0.2)] text-[hsl(var(--brass-2))]",
  staff: "bg-[hsl(var(--bg-2))] text-[hsl(var(--ink-soft))]",
};

export default function StaffUsers() {
  const { user: me } = useAuth();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [form, setForm] = useState({ email: "", password: "", name: "" });

  const load = async () => {
    try {
      const { data } = await api.get("/staff/users");
      setItems(data);
    } catch { toast.error("Could not load users."); }
    finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  const create = async (e) => {
    e.preventDefault();
    try {
      await api.post("/staff/users", form);
      toast.success("User added");
      setForm({ email: "", password: "", name: "" });
      setCreating(false);
      load();
    } catch (err) {
      const d = err?.response?.data?.detail;
      toast.error(typeof d === "string" ? d : "Create failed");
    }
  };

  const remove = async (u) => {
    if (u.email === me?.email) return;
    if (!window.confirm(`Delete ${u.email}?`)) return;
    try {
      await api.delete(`/staff/users/${u.id}`);
      setItems((prev) => prev.filter((x) => x.id !== u.id));
      toast.success("User deleted");
    } catch (err) {
      const d = err?.response?.data?.detail;
      toast.error(typeof d === "string" ? d : "Delete failed");
    }
  };

  const changeRole = async (u, role) => {
    try {
      const { data } = await api.patch(`/staff/users/${u.id}/role`, { role });
      setItems((prev) => prev.map((x) => (x.id === u.id ? data : x)));
      toast.success(`Role set to ${role}`);
    } catch (err) {
      const d = err?.response?.data?.detail;
      toast.error(typeof d === "string" ? d : "Role change failed");
    }
  };

  const isAdmin = me?.role === "admin";

  return (
    <div data-testid="staff-users">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <div className="eyebrow">Users</div>
          <h1 className="mt-2 text-4xl">Staff accounts</h1>
          <p className="mt-3 text-[hsl(var(--ink-soft))] max-w-xl">
            Every staff account can manage reservations, gallery, events, and the party menu. The
            primary admin (<code>{me?.email}</code>) cannot be deleted.
          </p>
        </div>
        <button data-testid="user-new-btn" onClick={() => setCreating((v) => !v)} className="btn-brass !py-2.5 !px-5 text-sm">
          <Plus size={14} /> {creating ? "Cancel" : "Add user"}
        </button>
      </div>

      {creating && (
        <form data-testid="user-new-form" onSubmit={create} className="mt-6 border border-[hsl(var(--brass))] bg-[hsl(var(--bg-2))] p-6 grid grid-cols-1 md:grid-cols-3 gap-4">
          <input required placeholder="Email" type="email" data-testid="new-user-email" className="user-inp" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} />
          <input required placeholder="Name" data-testid="new-user-name" className="user-inp" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
          <input required placeholder="Password (min 8)" type="password" data-testid="new-user-password" className="user-inp" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} />
          <div className="md:col-span-3">
            <button type="submit" data-testid="new-user-submit" className="btn-brass">Create user</button>
          </div>
        </form>
      )}

      {loading && <div className="mt-8 text-[hsl(var(--ink-soft))]">Loading...</div>}

      <div className="mt-8 border border-[hsl(var(--rule))]">
        <table className="w-full text-sm">
          <thead className="bg-[hsl(var(--bg-2))] text-left text-[10px] uppercase tracking-widest text-[hsl(var(--ink-soft))]">
            <tr><th className="p-3">Name</th><th className="p-3">Email</th><th className="p-3">Role</th><th className="p-3">Added</th><th className="p-3 text-right">Actions</th></tr>
          </thead>
          <tbody className="divide-y divide-[hsl(var(--rule))]">
            {items.map((u) => (
              <tr key={u.id} data-testid={`user-row-${u.id}`}>
                <td className="p-3">{u.name || "—"}</td>
                <td className="p-3">{u.email}</td>
                <td className="p-3">
                  {isAdmin && u.email !== me?.email ? (
                    <select
                      data-testid={`user-role-${u.id}`}
                      value={u.role}
                      onChange={(e) => changeRole(u, e.target.value)}
                      className={`text-[10px] uppercase tracking-widest px-2 py-1 rounded ${ROLE_STYLES[u.role] || ""} border-0 focus:outline-none`}
                    >
                      <option value="admin">Admin</option>
                      <option value="manager">Manager</option>
                      <option value="staff">Staff</option>
                    </select>
                  ) : (
                    <span className={`text-[10px] uppercase tracking-widest px-2 py-0.5 ${ROLE_STYLES[u.role] || "bg-[hsl(var(--bg-2))]"}`}>{u.role}</span>
                  )}
                </td>
                <td className="p-3 text-[hsl(var(--ink-soft))]">{new Date(u.created_at).toLocaleDateString()}</td>
                <td className="p-3 text-right">
                  {u.email === me?.email ? (
                    <span className="text-xs text-[hsl(var(--ink-soft))]">(you)</span>
                  ) : (
                    <button data-testid={`user-delete-${u.id}`} onClick={() => remove(u)} className="inline-flex items-center gap-1 text-xs text-[hsl(6_62%_45%)]">
                      <Trash2 size={12} /> Delete
                    </button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <style>{`
        .user-inp {
          background: hsl(var(--bg));
          border: 1px solid hsl(var(--rule));
          border-radius: 4px;
          padding: 0.6rem 0.85rem;
          font-family: inherit;
          font-size: 0.9rem;
          color: hsl(var(--ink));
        }
        .user-inp:focus { outline: none; border-color: hsl(var(--forest)); }
      `}</style>
    </div>
  );
}
